import { makeEmptyDropzoneId, parseDndId, TOP_LEVEL_END_ID } from './dndId';

// Render-time virtual override for the container drag-and-drop feature -
// canonical data (elements/containers, or elements/interactiveElements/
// containers on the exercise side) is NEVER mutated during a drag. Instead
// each list (top-level, or one container's children) recomputes its own
// rendered order every render by applying `dragPreview` as a pure overlay
// on top of whatever the canonical merge already produced. This makes
// "drag out without dropping" trivially correct (just clear dragPreview,
// nothing to roll back) and immune to an unrelated mid-drag refetch ever
// going stale, since canonical data is always the real source of truth.
//
// `dragPreview` shape: `{ activeId, activeEntry, overContainerId, overIndex } | null`.
// `activeEntry` travels WITH the preview state (captured once at drag-start
// from the full canonical universe) rather than being re-derived per list,
// since a list being hovered didn't necessarily originate the drag - it
// needs the full entry object to render the inserted preview item even
// when the active item lives in a completely different list right now.
//
// `scopes`: array of `{containerId, entries}` - one per list (top-level +
// every container, entries already `{dndId, ...}` shaped by the caller) -
// used by both buildDndIndex/resolveOver (below) and by drag-start/render
// code that needs to look an entry up by its dndId across the whole topic/
// exercise, not just one list.

// `entries`: this ONE list's own canonical entries. `ownerContainerId`:
// which list this is (a real container id, or null for top-level).
// The inserted preview entry is tagged `isDropPlaceholder: true` rather
// than being the real active entry rendered in full - a real card's size
// varies with its content (toolbar, editor state, media height...), and
// inserting/removing something that size-variable as the pointer moves
// creates a feedback loop with collision detection: the insertion grows
// the target list, which shifts sibling rects, which can move the
// (stationary) pointer outside the sibling it was just resolving against,
// flipping the resolution back out, shrinking the list again, moving the
// sibling back, flipping back in - oscillating every frame. A small
// fixed-size placeholder (rendered by the caller, not this file) avoids
// that: the list only ever grows/shrinks by one constant, small amount
// regardless of what's actually being dragged.
export function applyDragPreview(entries, dragPreview, ownerContainerId) {
   if (!dragPreview) return entries;
   const { activeId, activeEntry, overContainerId, overIndex } = dragPreview;
   const withoutActive = entries.filter((entry) => entry.dndId !== activeId);
   if (overContainerId !== ownerContainerId) return withoutActive;
   if (!activeEntry) return withoutActive;
   const placeholder = { ...activeEntry, dndId: activeId, isDropPlaceholder: true };
   const clampedIndex = Math.max(0, Math.min(overIndex, withoutActive.length));
   return [...withoutActive.slice(0, clampedIndex), placeholder, ...withoutActive.slice(clampedIndex)];
}

export function findEntryInScopes(scopes, dndId) {
   for (const scope of scopes) {
      const found = scope.entries.find((entry) => entry.dndId === dndId);
      if (found) return found;
   }
   return null;
}

// One dndId -> {containerId, index} lookup built fresh every render from
// every list's OWN canonical entries (never the drag-preview-adjusted
// ones - resolving `over` should always be against real positions, not a
// preview of itself).
export function buildDndIndex(scopes) {
   const index = new Map();
   scopes.forEach(({ containerId, entries }) => {
      entries.forEach((entry, i) => {
         index.set(entry.dndId, { containerId, index: i });
      });
      index.set(makeEmptyDropzoneId(containerId), { containerId, index: 0 });
      if (containerId === null) index.set(TOP_LEVEL_END_ID, { containerId: null, index: entries.length });
   });
   return index;
}

// Resolves a dnd-kit `over.id` into `{containerId, index}` - or null when
// there's nothing valid under the pointer, or the pointer is over the item
// being dragged itself (a no-op, not a real hover target).
//
// Handles one subtlety that's easy to get off-by-one on: when the hovered
// list is the SAME list the active item already canonically lives in, the
// resolved index is measured against the list BEFORE the active item is
// removed - but applyDragPreview inserts into the list AFTER removing it
// (see `withoutActive` above). So hovering an item that sits AFTER the
// active item's own canonical slot needs its index shifted down by one, or
// every same-list reorder would land one slot further than intended.
export function resolveOver(overId, dndIndex, activeId, scopes) {
   if (!overId || overId === activeId) return null;
   // A container's own top-level slot (`useSortable({disabled:true})`)
   // spans its ENTIRE card - toolbar, body, add-button, everything - not
   // just "the gap right before/after it in the top-level list". Treating
   // it as a top-level insertion point caused a real oscillation bug: for
   // an EMPTY container there's no child to prefer, only a thin empty-
   // dropzone strip inside it - hovering anywhere else in the card (still
   // "clearly inside the container" to a user) fell through to this outer
   // slot id, which inserted a top-level placeholder BEFORE the container,
   // shifting its geometry out from under a stationary pointer and
   // flipping the resolution back and forth every re-render. The same
   // ambiguity exists for a non-empty container's own padding/toolbar, just
   // masked there by a specific child usually being hit first. Hovering a
   // container's own slot id should always mean "into this container" (at
   // the end of its current children), never "top-level, near me".
   const { kind, id } = parseDndId(String(overId));
   if (kind === 'container') {
      const targetScope = scopes.find((scope) => scope.containerId === Number(id));
      if (targetScope) return { containerId: targetScope.containerId, index: targetScope.entries.length };
   }
   const resolved = dndIndex.get(overId);
   if (!resolved) return null;
   let overIndex = resolved.index;
   const targetScope = scopes.find((scope) => scope.containerId === resolved.containerId);
   if (targetScope) {
      const activeCanonicalIndex = targetScope.entries.findIndex((entry) => entry.dndId === activeId);
      if (activeCanonicalIndex !== -1 && activeCanonicalIndex < overIndex) {
         overIndex -= 1;
      }
   }
   return { containerId: resolved.containerId, index: overIndex };
}
